// Pure deploy rules for the new-app wizard. No React; `@/…` imports are
// type-only so this module runs under `node --experimental-strip-types`.
import { endpointRemainingCapacity } from './dashboardCompute.ts';
import type { EndpointWithResources } from '@/types/endpoint';

export type TargetMode = 'auto' | 'locked';

/** Minimal structural view of a catalog stack (only the fields used here). */
export interface StackLike {
  cpu_limit: number;
  memory_mb: number;
  storage_gb: number;
  /** Server skips the CPU capacity check for shared-CPU stacks. */
  shared_cpu?: boolean;
  target_mode?: TargetMode | null;
  locked_endpoints?: number[];
  security_blocks?: {
    id: number;
    name: string;
    description: string;
    is_enabled_default: boolean;
    sort_order: number;
    selection?: boolean;
  }[];
}

/** Minimal structural view of a catalog (only the fields used here). */
export interface CatalogLike {
  expose_port?: number;
  target_mode?: TargetMode | null;
  locked_endpoints?: number[];
}

export interface Shortfall {
  cpu: number;
  memoryMb: number;
  storageGb: number;
}

// Shortfalls round UP so a tiny gap never reads as zero; the round() first
// removes float noise (1 - 0.7 = 0.30000000000000004 must read 0.3, not 0.4).
const upTenth = (n: number): number => Math.ceil(Math.round(n * 1e6) / 1e5) / 10;
const upInt = (n: number): number => Math.ceil(Math.round(n * 1e6) / 1e6);

/** "Needs 0.5 more cores, 256 MB more memory"; "Insufficient resources" when nothing is missing. */
export function describeShortfall(s: Shortfall): string {
  const parts: string[] = [];
  const positive = (n: number): boolean => Number.isFinite(n) && n > 0;
  const cpu = upTenth(s.cpu);
  if (positive(cpu)) parts.push(`${cpu} more ${cpu === 1 ? 'core' : 'cores'}`);
  const mb = upInt(s.memoryMb);
  if (positive(mb)) parts.push(mb >= 1024 ? `${upTenth(mb / 1024).toFixed(1)} GB more memory` : `${mb} MB more memory`);
  const gb = upTenth(s.storageGb);
  if (positive(gb)) parts.push(`${gb} GB more storage`);
  return parts.length ? `Needs ${parts.join(', ')}` : 'Insufficient resources';
}

export interface EndpointFit {
  endpoint: EndpointWithResources;
  sufficient: boolean;
  shortfall: Shortfall;
  remaining: { cpu: number; memoryMb: number; storageGb: number; hasTotals: boolean };
  allowed: boolean;
}

// --- validators (product rules, preserved from the old page) ---------------

export const validateAppName = (s: string): boolean =>
  /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(s) && s.length <= 64;

export const validateExternalUserId = (s: string): boolean =>
  /^[a-zA-Z0-9_-]+$/.test(s) && s.length <= 128;

export const validateDisplayName = (s: string): boolean => {
  const t = s.trim();
  if (!t) return true;
  return /^[A-Za-z ]+$/.test(t) && t.length <= 255;
};

export const validateEnvVarKey = (s: string): boolean =>
  /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(s) && s.length > 0 && s.length <= 64;

// --- target mode -------------------------------------------------------------

/** stack override -> catalog default -> auto. No stack selected -> auto. */
export function effectiveTargetMode(stack: StackLike | null, catalog: CatalogLike | null): TargetMode {
  if (!stack) return 'auto';
  return stack.target_mode ?? catalog?.target_mode ?? 'auto';
}

/** Stack's list when the stack overrides (non-null target_mode), else the catalog's. */
export function lockedEndpointIds(stack: StackLike | null, catalog: CatalogLike | null): Set<number> {
  if (!stack) return new Set<number>();
  if (stack.target_mode !== null && stack.target_mode !== undefined) {
    return new Set(stack.locked_endpoints ?? []);
  }
  return new Set(catalog?.locked_endpoints ?? []);
}

// --- capacity fit ------------------------------------------------------------

export function endpointFit(
  ep: EndpointWithResources,
  stack: StackLike | null,
  allowedIds: Set<number> | null,
): EndpointFit {
  const remaining = endpointRemainingCapacity(ep);
  const allowed = allowedIds === null || allowedIds.has(ep.id);
  const zero: Shortfall = { cpu: 0, memoryMb: 0, storageGb: 0 };

  if (!stack || (!remaining.hasTotals && !ep.resources)) {
    return { endpoint: ep, sufficient: true, shortfall: zero, remaining, allowed };
  }
  const shortfall: Shortfall = {
    cpu: stack.shared_cpu ? 0 : Math.max(0, stack.cpu_limit - remaining.cpu),
    memoryMb: Math.max(0, stack.memory_mb - remaining.memoryMb),
    storageGb: Math.max(0, stack.storage_gb - remaining.storageGb),
  };
  const sufficient = shortfall.cpu === 0 && shortfall.memoryMb === 0 && shortfall.storageGb === 0;
  return { endpoint: ep, sufficient, shortfall, remaining, allowed };
}

const byName = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/** Only `up` endpoints, stable order by name. Does not mutate the input. */
export function candidateEndpoints(endpoints: EndpointWithResources[]): EndpointWithResources[] {
  return endpoints.filter((ep) => ep.status === 'up').sort((a, b) => byName(a.name, b.name));
}

export function rankEndpoints(
  endpoints: EndpointWithResources[],
  stack: StackLike | null,
  mode: TargetMode,
  locked: Set<number>,
): { fits: EndpointFit[]; recommended: EndpointFit | null } {
  const allowedIds = mode === 'locked' ? locked : null;
  let fits = candidateEndpoints(endpoints).map((ep) => endpointFit(ep, stack, allowedIds));
  if (mode === 'locked') fits = fits.filter((f) => f.allowed);

  const sufficient = fits
    .filter((f) => f.sufficient)
    .sort(
      (a, b) =>
        b.remaining.cpu - a.remaining.cpu ||
        b.remaining.memoryMb - a.remaining.memoryMb ||
        byName(a.endpoint.name, b.endpoint.name),
    );
  const insufficient = fits
    .filter((f) => !f.sufficient)
    .sort((a, b) => byName(a.endpoint.name, b.endpoint.name));

  return { fits: [...sufficient, ...insufficient], recommended: sufficient[0] ?? null };
}

/** Locked mode with exactly one sufficient fit -> its id; otherwise null. */
export function preselectedEndpointId(fits: EndpointFit[], mode: TargetMode): number | null {
  if (mode !== 'locked') return null;
  const ok = fits.filter((f) => f.sufficient);
  return ok.length === 1 ? ok[0].endpoint.id : null;
}

// --- gates and derived values -------------------------------------------------

export function traefikBlocked(catalog: CatalogLike | null, endpoint: EndpointWithResources | null): boolean {
  return (catalog?.expose_port ?? 0) > 0 && endpoint != null && !endpoint.traefik_deployed;
}

export function effectiveSecurityBlocks(
  stack: StackLike | null,
): { id: number; name: string; description: string; enabled: boolean }[] {
  return [...(stack?.security_blocks ?? [])]
    .sort((a, b) => a.sort_order - b.sort_order || byName(a.name, b.name))
    .map((b) => ({
      id: b.id,
      name: b.name,
      description: b.description,
      enabled: b.selection ?? b.is_enabled_default,
    }));
}

/** `code` is the API's error.details.code; known codes win over message matching. */
export function mapDeployError(message: string, hasExplicitEndpoint: boolean, code?: string): string {
  if (code === 'INVALID_SUBDOMAIN') return `Invalid subdomain: ${message.replace(/^invalid subdomain:\s*/i, '').trim()}`;
  if (code === 'SUBDOMAIN_NOT_ROUTABLE') {
    return 'This app gets no hostname (no domain, or the catalog exposes no port), so a subdomain cannot be set.';
  }
  if (code === 'DOMAIN_NOT_FOUND') return 'The selected domain no longer exists. Choose another domain.';
  if (code === 'ORIGIN_TLS_NOT_READY') {
    return `The ${hasExplicitEndpoint ? 'selected' : 'chosen'} endpoint doesn't serve this domain's origin certificate yet. Push it from Settings → Domains, or choose another endpoint or domain.`;
  }
  if (message.includes('already exists')) return 'App name already exists. Try a different name.';
  if (message.includes('insufficient resources')) {
    return hasExplicitEndpoint
      ? 'Selected endpoint has insufficient resources for this stack.'
      : 'No endpoint has sufficient resources for this stack.';
  }
  return message.trim() || 'Failed to deploy';
}

/** The wizard's domain choice if it can still be made: listed, with more than one domain; else '' (default). */
export function effectiveDomainChoice(domain: string, domains: readonly { name: string }[]): string {
  return domains.length > 1 && domains.some((d) => d.name === domain) ? domain : '';
}

/** What the app's domain will be: the choice, or the default domain for '' (null when none is known). */
export function domainChoiceLabel(domain: string, domains: readonly { name: string; is_default: boolean }[]): string | null {
  if (domain) return domain;
  const def = domains.find((d) => d.is_default);
  return def ? `${def.name} (default)` : null;
}

// Preview only: the server generates the real name (seconds-based, sanitised).
export function generatedInstanceName(catalogName: string, externalUserId: string, nowMs: number): string {
  if (!catalogName || !externalUserId) return '';
  return `${catalogName}-${externalUserId}-${nowMs}`;
}
